import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LecturerSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsOpen(false); // Close mobile menu when switching to desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
      navigate('/login');
    }
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };
  
  const menuItems = [
    { 
      path: '/lecturer-dashboard', 
      label: 'Dashboard',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z" fill="currentColor"/>
      </svg>
    },
    {
      path: '/qr',
      label: 'Attendance',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="currentColor"/>
      </svg>
    },
    {
      path: '/lecturer/students',
      label: 'Students',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
      </svg>
    },
    {
      path: '/lecturer/profile',
      label: 'Profile',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 5C13.66 5 15 6.34 15 8C15 9.66 13.66 11 12 11C10.34 11 9 9.66 9 8C9 6.34 10.34 5 12 5ZM12 19.2C9.5 19.2 7.29 17.92 6 15.98C6.03 13.99 10 12.9 12 12.9C13.99 12.9 17.97 13.99 18 15.98C16.71 17.92 14.5 19.2 12 19.2Z" fill="currentColor"/>
      </svg>
    },
  ];

  return (
    <>
      {/* Mobile Hamburger Button */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            top: '15px',
            left: '15px',
            zIndex: 1002,
            background: '#1A1C1F',
            border: 'none',
            borderRadius: '8px',
            padding: '12px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => e.target.style.background = '#2A2C2F'}
          onMouseLeave={(e) => e.target.style.background = '#1A1C1F'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 18H21V16H3V18ZM3 13H21V11H3V13ZM3 6V8H21V6H3Z" fill="white"/>
          </svg>
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          onClick={closeSidebar}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Sidebar */}
      <div style={{
        width: isMobile ? '280px' : '290px',
        height: '100vh',
        position: 'fixed',
        left: isMobile ? (isOpen ? '0' : '-280px') : '0',
        top: 0,
        background: '#1A1C1F',
        display: 'flex',
        flexDirection: 'column',
        padding: isMobile ? '60px 0 20px 0' : '20px 0',
        fontFamily: 'Outfit, sans-serif',
        zIndex: 1001,
        transition: 'left 0.3s ease',
        boxShadow: isMobile ? '4px 0 20px rgba(0, 0, 0, 0.1)' : 'none'
      }}>
        {/* Close Button for Mobile */}
        {isMobile && (
          <button
            onClick={closeSidebar}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              transition: 'background 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
            </svg>
          </button>
        )}

        {/* Logo */}
        <div style={{
          padding: isMobile ? '10px 30px' : '20px 30px',
          marginBottom: isMobile ? 30 : 40,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Link 
            to="/lecturer-dashboard" 
            onClick={isMobile ? closeSidebar : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none'
            }}
          >
            <img 
              src="/assets/blackbg.jpg" 
              alt="AttenZef Logo"
              style={{
                height: '50px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Link>
        </div>

        {/* Navigation Menu */}
        <nav style={{flex: 1, overflowY: 'auto'}}>
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={isMobile ? closeSidebar : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: isMobile ? '18px 30px' : '15px 40px',
                textDecoration: 'none',
                color: location.pathname === item.path ? '#F04915' : '#FFFFFF',
                background: location.pathname === item.path ? '#FFFFFF' : 'transparent',
                fontSize: isMobile ? 15 : 16,
                fontFamily: 'Outfit',
                fontWeight: 500,
                marginBottom: isMobile ? 8 : 10,
                gap: 12,
                transition: 'all 0.3s ease',
                marginLeft: isMobile ? '10px' : '0',
                marginRight: isMobile ? '10px' : '0',
                borderRadius: isMobile ? '8px' : '0',
                minHeight: '48px' // Touch-friendly minimum height
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== item.path) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== item.path) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Sign Out Button */}
        <div style={{padding: isMobile ? '15px 0' : '20px 0'}}>
          <button
            onClick={() => {
              handleSignOut();
              if (isMobile) closeSidebar();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: isMobile ? '18px 30px' : '12px 40px',
              background: 'transparent',
              border: 'none',
              color: '#F04915',
              fontSize: isMobile ? 15 : 16,
              fontFamily: 'Outfit',
              fontWeight: 500,
              cursor: 'pointer',
              gap: 12,
              transition: 'all 0.3s ease',
              width: '100%',
              marginLeft: isMobile ? '10px' : '0',
              marginRight: isMobile ? '10px' : '0',
              borderRadius: isMobile ? '8px' : '0',
              minHeight: '48px' // Touch-friendly minimum height
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(240, 73, 21, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.58L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
            </svg>
            Sign Out
          </button>

          {/* Powered by section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 8,
            marginTop: 16,
            padding: isMobile ? '0 30px' : '0 40px'
          }}>
            <span style={{
              fontSize: isMobile ? 14 : 16,
              fontFamily: 'Outfit',
              color: '#888',
              fontWeight: 500
            }}>
              Powered by ZEFTACK
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default LecturerSidebar;
